type AsyncVoidFunction<T extends unknown[]> = (...args: T) => Promise<void>;

// Wraps a side-effectful async function to ensure that any simultaneous calls get combined.
// The wrapped function takes no arguments, and returns a void promise (which can still throw).
// Any calls to the wrapper will either trigger a new call of the wrapped function, or if a call
// if in progress, will return a promise for the existing call.
// This is useful for doing transactional, side-effectful communication with the server, where
// things will break if multiple calls to the same endpoints are made simultaneously.
export const combineAsyncCalls = <T extends unknown[]>(
  func: AsyncVoidFunction<T>,
): AsyncVoidFunction<T> => {
  let currentPromise: Promise<void> | null = null;

  const callWithCleanup = async (...args: T) => {
    try {
      await func(...args);
    } finally {
      currentPromise = null;
    }
  };

  return (...args: T) => {
    if (!currentPromise) {
      currentPromise = callWithCleanup(...args);
    }

    return currentPromise;
  };
};
